// Field operations: territories, the day's plan, follow-ups and targets.
//
// This sits on top of the tracking module that already exists. Those records answer "where was the
// rep and what did they do"; these answer "what were they meant to do, what are they chasing, and
// how are they doing against it".
//
// Nothing here is a second copy of a clinic, doctor, case, invoice or payment. Every figure a rep
// or a manager sees is counted from the records the lab already keeps, so a rep's achievement and
// the lab's books are the same number read two ways — there is no way for them to disagree.
import { z } from 'zod';

/* -- territories --------------------------------------------------------------------------------- */

export interface FieldTerritory {
  id: string;
  code: string;
  name: string;
  region: string | null;
  area: string | null;
  city: string | null;
  managerId: string | null;
  managerName: string | null;
  notes: string | null;
  isActive: boolean;
  /** Counts, so a territory list is readable without opening each one. */
  repCount: number;
  clinicCount: number;
  createdAt: string;
  updatedAt: string;
}

export const territorySchema = z.object({
  code: z.string().trim().min(1, 'Give the territory a code').max(24),
  name: z.string().trim().min(2, 'Name the territory').max(120),
  region: z.string().trim().max(80).nullish(),
  area: z.string().trim().max(80).nullish(),
  city: z.string().trim().max(80).nullish(),
  managerId: z.string().nullish(),
  notes: z.string().trim().max(1000).nullish(),
  isActive: z.boolean().optional(),
});
export type TerritoryInput = z.infer<typeof territorySchema>;

/** Who covered what, and when. Never deleted — a rep moving territory doesn't rewrite the past. */
export interface RepAssignment {
  id: string;
  repId: string;
  repName: string | null;
  territoryId: string;
  territoryName: string | null;
  fromDate: string;
  toDate: string | null;
  note: string | null;
  createdAt: string;
}

export const repAssignmentSchema = z.object({
  repId: z.string().min(1),
  territoryId: z.string().min(1),
  fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date'),
  note: z.string().trim().max(400).nullish(),
});

/* -- clinic and doctor sales judgement ------------------------------------------------------------ */

export const CLINIC_CLASSIFICATIONS = ['a', 'b', 'c', 'strategic', 'new', 'inactive', 'growth'] as const;
export type ClinicClassification = (typeof CLINIC_CLASSIFICATIONS)[number];
export const CLINIC_CLASSIFICATION_LABELS: Record<ClinicClassification, string> = {
  a: 'A - top',
  b: 'B - steady',
  c: 'C - occasional',
  strategic: 'Strategic',
  new: 'New',
  inactive: 'Inactive',
  growth: 'Growth opportunity',
};

export const POTENTIALS = ['high', 'medium', 'low'] as const;
export type Potential = (typeof POTENTIALS)[number];
export const POTENTIAL_LABELS: Record<Potential, string> = { high: 'High', medium: 'Medium', low: 'Low' };

export const DOCTOR_SPECIALTIES = [
  'orthodontist',
  'general',
  'prosthodontist',
  'oral_surgeon',
  'pediatric',
  'periodontist',
  'implantologist',
  'other',
] as const;
export type DoctorSpecialty = (typeof DOCTOR_SPECIALTIES)[number];
export const DOCTOR_SPECIALTY_LABELS: Record<DoctorSpecialty, string> = {
  orthodontist: 'Orthodontist',
  general: 'General dentist',
  prosthodontist: 'Prosthodontist',
  oral_surgeon: 'Oral surgeon',
  pediatric: 'Pediatric dentist',
  periodontist: 'Periodontist',
  implantologist: 'Implantologist',
  other: 'Other',
};

/** A clinic a rep is responsible for. What they plan their day from. */
export interface RepClinic {
  id: string;
  name: string;
  city: string | null;
  classification: ClinicClassification | null;
  potential: Potential | null;
  /** The geofence, if the lab has placed one on this clinic. */
  placeId: string | null;
  /** When this rep last stood in it. Null means never — which is the interesting case. */
  lastVisitAt: string | null;
  /** The clinic's portal username, once onboarding credentials exist. */
  portalUsername: string | null;
}

/** Onboarding credentials a rep hands to a clinic. The password is never stored in plaintext. */
export interface RepClinicOnboarding {
  clinicId: string;
  clinicName: string;
  username: string;
  password: string;
  /** True when a login already existed and its password was replaced. */
  reset: boolean;
}

/**
 * A clinic a rep is standing in for the first time.
 *
 * The coordinates are the device's, taken at the moment of registering — which is the whole point:
 * a clinic added from a desk has an address somebody typed, while one punched in the field has a
 * pin the lab can navigate to and geofence against. The accuracy comes along so a vague fix can be
 * shown as vague rather than presented as a confident dot.
 */
export const repClinicRegisterSchema = z.object({
  name: z.string().trim().min(2, 'What is the clinic called?').max(160),
  clientType: z.enum(['dentist', 'clinic', 'hospital']).default('clinic'),
  dentistName: z.string().trim().max(160).nullish(),
  contactPerson: z.string().trim().max(160).nullish(),
  mobile: z.string().trim().max(40).nullish(),
  whatsapp: z.string().trim().max(40).nullish(),
  email: z.string().trim().max(160).nullish(),
  city: z.string().trim().max(80).nullish(),
  address: z.string().trim().max(400).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  potential: z.enum(POTENTIALS).nullish(),
  lat: z.coerce.number().min(-90).max(90),
  lng: z.coerce.number().min(-180).max(180),
  accuracyM: z.coerce.number().min(0).max(100_000).nullish(),
  /** Bypass the "there is already a clinic here" guard, once a person has looked at the match. */
  confirmDuplicate: z.boolean().optional(),
});
export type RepClinicRegisterInput = z.infer<typeof repClinicRegisterSchema>;

/** A clinic on the map: where it is, who covers it, and when it was last called on. */
export interface ClinicPin {
  id: string;
  name: string;
  city: string | null;
  lat: number;
  lng: number;
  accuracyM: number | null;
  classification: ClinicClassification | null;
  potential: Potential | null;
  repId: string | null;
  repName: string | null;
  territoryName: string | null;
  lastVisitAt: string | null;
  /** Visits logged against this clinic, all time. */
  visitCount: number;
  /** Stays the geofence recorded here (a rep standing inside it for a real stop), all time. */
  stayCount: number;
  /** Total seconds reps have spent at this clinic, from the geofence. */
  stayTotalS: number;
  lastStayAt: string | null;
  lastStayS: number | null;
  /** Added from the field rather than typed at a desk. */
  addedFromField: boolean;
  createdAt: string;
}

/** One recorded stay at a clinic: who, when, how long, and what they logged while there. */
export interface ClinicStay {
  id: string;
  repId: string;
  repName: string;
  startedAt: string;
  endedAt: string;
  durationS: number;
  isOpen: boolean;
  visitCount: number;
}

/** Everything the lab wants to know about one clinic's foot traffic. */
export interface ClinicActivity {
  clinic: { id: string; name: string; city: string | null; lat: number | null; lng: number | null; repName: string | null };
  stays: ClinicStay[];
  visits: Array<{ id: string; repId: string; repName: string; at: string; purpose: string; outcome: string; contactName: string | null; notes: string | null }>;
  totals: { stays: number; stayTotalS: number; visits: number; lastStayAt: string | null; lastVisitAt: string | null; avgStayS: number };
}

/**
 * A clinic as the case-intake picker sees it.
 *
 * Deliberately carries the doctor group alongside the clinic: on intake, choosing the clinic should
 * settle who is billed without anybody typing a code from memory. A clinic that has no doctor group
 * yet says so, rather than silently producing a case nobody can be invoiced for.
 */
export interface ClinicOption {
  id: string;
  clinicCode: string;
  name: string;
  city: string | null;
  dentistName: string | null;
  contactPerson: string | null;
  mobile: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  /** The doctor group this clinic bills under, when one is linked. */
  doctorId: string | null;
  doctorCode: string | null;
  doctorName: string | null;
  /** Cases already on the books for this clinic — context when picking between similar names. */
  caseCount: number;
}

/* -- the day's plan ------------------------------------------------------------------------------- */

export const PLANNED_VISIT_STATUSES = ['planned', 'done', 'missed', 'cancelled'] as const;
export type PlannedVisitStatus = (typeof PLANNED_VISIT_STATUSES)[number];
export const PLANNED_VISIT_STATUS_LABELS: Record<PlannedVisitStatus, string> = {
  planned: 'Planned',
  done: 'Done',
  missed: 'Missed',
  cancelled: 'Cancelled',
};

export const VISIT_PURPOSES = [
  'relationship',
  'case_review',
  'new_business',
  'demo',
  'training',
  'payment',
  'complaint',
  'delivery',
  'other',
] as const;
export type VisitPurpose = (typeof VISIT_PURPOSES)[number];
export const VISIT_PURPOSE_LABELS: Record<VisitPurpose, string> = {
  relationship: 'Relationship call',
  case_review: 'Case review',
  new_business: 'New business',
  demo: 'Product demonstration',
  training: 'Training',
  payment: 'Payment follow-up',
  complaint: 'Complaint',
  delivery: 'Delivery',
  other: 'Other',
};

export interface PlannedVisit {
  id: string;
  repId: string;
  repName: string | null;
  planDate: string;
  clinicId: string | null;
  clinicName: string;
  doctorId: string | null;
  doctorName: string | null;
  placeId: string | null;
  plannedTime: string | null;
  purpose: VisitPurpose;
  priority: 'normal' | 'high';
  status: PlannedVisitStatus;
  /** The logged visit that satisfied this plan. Done-ness is evidence, never a checkbox. */
  visitId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const plannedVisitSchema = z.object({
  repId: z.string().nullish(),
  planDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date'),
  clinicId: z.string().nullish(),
  clinicName: z.string().trim().min(1, 'Which clinic?').max(160),
  doctorId: z.string().nullish(),
  placeId: z.string().nullish(),
  plannedTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a HH:MM time').nullish(),
  purpose: z.enum(VISIT_PURPOSES).default('relationship'),
  priority: z.enum(['normal', 'high']).default('normal'),
  notes: z.string().trim().max(1000).nullish(),
});
export type PlannedVisitInput = z.infer<typeof plannedVisitSchema>;

/* -- follow-ups ----------------------------------------------------------------------------------- */

export const FOLLOWUP_KINDS = [
  'missing_documents',
  'plan_discussion',
  'doctor_approval',
  'payment',
  'production_update',
  'dispatch_update',
  'delivery_confirmation',
  'refinement',
  'retainer',
  'relationship',
  'other',
] as const;
export type FollowupKind = (typeof FOLLOWUP_KINDS)[number];
export const FOLLOWUP_KIND_LABELS: Record<FollowupKind, string> = {
  missing_documents: 'Missing records',
  plan_discussion: 'Treatment plan discussion',
  doctor_approval: 'Doctor approval',
  payment: 'Payment reminder',
  production_update: 'Production update',
  dispatch_update: 'Dispatch update',
  delivery_confirmation: 'Delivery confirmation',
  refinement: 'Refinement',
  retainer: 'Retainer',
  relationship: 'Relationship',
  other: 'Other',
};

export const FOLLOWUP_STATUSES = ['open', 'in_progress', 'completed', 'snoozed', 'cancelled', 'escalated'] as const;
export type FollowupStatus = (typeof FOLLOWUP_STATUSES)[number];
export const FOLLOWUP_STATUS_LABELS: Record<FollowupStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
  snoozed: 'Snoozed',
  cancelled: 'Cancelled',
  escalated: 'Escalated',
};

export interface Followup {
  id: string;
  repId: string | null;
  repName: string | null;
  assignedTo: string | null;
  clinicId: string | null;
  clinicName: string | null;
  doctorId: string | null;
  doctorName: string | null;
  caseId: string | null;
  caseNumber: string | null;
  patientName: string | null;
  kind: FollowupKind;
  title: string;
  dueDate: string;
  priority: 'low' | 'normal' | 'high';
  status: FollowupStatus;
  notes: string | null;
  outcome: string | null;
  nextDueDate: string | null;
  /** Past its due date and still open — what a rep's day should start with. */
  isOverdue: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');

export const followupSchema = z.object({
  repId: z.string().nullish(),
  clinicId: z.string().nullish(),
  doctorId: z.string().nullish(),
  caseId: z.string().nullish(),
  visitId: z.string().nullish(),
  kind: z.enum(FOLLOWUP_KINDS).default('relationship'),
  title: z.string().trim().min(2, 'What needs chasing?').max(200),
  dueDate: dateStr,
  priority: z.enum(['low', 'normal', 'high']).default('normal'),
  notes: z.string().trim().max(2000).nullish(),
});
export type FollowupInput = z.infer<typeof followupSchema>;

export const followupPatchSchema = z.object({
  status: z.enum(FOLLOWUP_STATUSES).optional(),
  outcome: z.string().trim().max(2000).nullish(),
  notes: z.string().trim().max(2000).nullish(),
  dueDate: dateStr.optional(),
  nextDueDate: dateStr.nullish(),
  priority: z.enum(['low', 'normal', 'high']).optional(),
});

/* -- targets -------------------------------------------------------------------------------------- */

export const TARGET_METRICS = ['revenue', 'cases', 'visits', 'new_clinics', 'collection'] as const;
export type TargetMetric = (typeof TARGET_METRICS)[number];

export const TARGET_METRIC_LABELS: Record<TargetMetric, string> = {
  revenue: 'Revenue invoiced',
  cases: 'Cases submitted',
  visits: 'Clinic visits',
  new_clinics: 'New clinics',
  collection: 'Payments collected',
};

/** Money metrics are paisa; the rest are plain counts. The UI has to know which it is showing. */
export const isMoneyMetric = (m: TargetMetric): boolean => m === 'revenue' || m === 'collection';

export interface FieldTarget {
  id: string;
  scope: 'rep' | 'territory' | 'lab';
  scopeId: string | null;
  scopeName: string | null;
  metric: TargetMetric;
  periodStart: string;
  periodEnd: string;
  targetValue: number;
  notes: string | null;
  createdAt: string;
}

/** A target with the reality counted against it. Achievement is never stored — always counted. */
export interface TargetProgress extends FieldTarget {
  achieved: number;
  remaining: number;
  /** 0-100+, so beating a target reads as beating it rather than capping at 100. */
  percent: number;
  /**
   * Where the period is up to, 0-100. A rep at 40% of target on day 20 of 30 is behind; the same
   * rep on day 10 is ahead. The number is meaningless without it.
   */
  periodElapsedPercent: number;
  onTrack: boolean;
}

export const targetSchema = z
  .object({
    scope: z.enum(['rep', 'territory', 'lab']),
    scopeId: z.string().nullish(),
    metric: z.enum(TARGET_METRICS),
    periodStart: dateStr,
    periodEnd: dateStr,
    /** Rupees for money metrics — the server converts. Nobody types paisa into a form. */
    targetValue: z.coerce.number().min(1, 'A target of nothing is not a target').max(10_000_000_000),
    notes: z.string().trim().max(500).nullish(),
  })
  .refine((t) => t.periodEnd >= t.periodStart, { message: 'The period ends before it starts', path: ['periodEnd'] })
  .refine((t) => t.scope === 'lab' || !!t.scopeId, { message: 'Choose who this target is for', path: ['scopeId'] });
export type TargetInput = z.infer<typeof targetSchema>;

/* -- the rep's day -------------------------------------------------------------------------------- */

/** Everything a field rep's home screen shows, in one fetch. */
export interface RepDashboard {
  repId: string;
  repName: string;
  repCode: string;
  territoryName: string | null;
  date: string;
  onDuty: boolean;
  /** Today's plan, and what has actually happened against it. */
  plannedToday: number;
  doneToday: number;
  missedToday: number;
  unplannedToday: number;
  plan: PlannedVisit[];
  followupsDue: Followup[];
  overdueFollowups: number;
  /** Counted from the lab's own records, not from anything the rep types. */
  clinicsAssigned: number;
  casesThisMonth: number;
  targets: TargetProgress[];
  /** Month to date, in paisa. */
  revenueThisMonth: number;
  collectedThisMonth: number;
}

/** The manager's view of a team, one row per rep. */
export interface TeamRow {
  repId: string;
  repName: string;
  repCode: string;
  territoryName: string | null;
  onDuty: boolean;
  plannedToday: number;
  doneToday: number;
  visitsThisMonth: number;
  clinicsAssigned: number;
  casesThisMonth: number;
  revenueThisMonth: number;
  openFollowups: number;
  overdueFollowups: number;
  /** Against the rep's revenue target for the month, when one is set. */
  revenueTarget: number | null;
  revenuePercent: number | null;
}
